import { Address } from 'viem'
import isNumber from 'lodash/isNumber'
import isNil from 'lodash/isNil'
import BigNumber from 'bignumber.js'
import { convertDecimals, cutDecimalsWithoutRounding } from '@utils/number.utils.ts'

export const formatAddress = (address: Address, limit = 5, endLimit?: number) => {
  endLimit = endLimit || limit
  if (address.length < limit) {
    return address
  }

  return `${address.substring(0, limit)}...${address.substring(address.length - endLimit)}`
}

export function formatNumber(value: bigint | number, decimals = 0) {
  if (isNumber(value)) {
    return value.toLocaleString('en-US')
  }

  return convertDecimals(value, decimals).toLocaleString('en-US')
}

export function formatCryptoNumber(value: bigint | number): string {
  return cutDecimalsWithoutRounding(value).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
  })
}

// this is custom rounding for USDC, usually for crypto we show 3 decimal places,
// but USDC is an exception and we show 2 decimal places
export function formatUSDCNumber(value: bigint | number, decimals = 2): string {
  return cutDecimalsWithoutRounding(value, 2).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  })
}

export const formatNumberWithoutDecimals = (value: number) => {
  return value.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })
}

export function isCryptoNumbersEqual(value1: number, value2: number) {
  const valueRounded = parseFloat(formatCryptoNumber(value1).replace(/,/g, ''))
  const value2Rounded = parseFloat(formatCryptoNumber(value2).replace(/,/g, ''))

  return new BigNumber(valueRounded).comparedTo(value2Rounded) === 0
}

export function formatNumberWithShortcuts(value: string | number) {
  const valueNumber = Number(value)

  if (valueNumber >= 1_000_000_000) {
    const value = (valueNumber / 1_000_000_000).toFixed(2)
    const integerPartFormatted = formatNumber(parseInt(value.split('.')[0]))
    const decimalPart = value.split('.')[1]

    return `${integerPartFormatted}${decimalPart === '00' ? '' : '.' + decimalPart}B`
  }

  if (valueNumber >= 1_000_000) {
    const value = (valueNumber / 1_000_000).toFixed(2)
    const integerPartFormatted = formatNumber(parseInt(value.split('.')[0]))
    const decimalPart = value.split('.')[1]

    return `${integerPartFormatted}${decimalPart === '00' ? '' : '.' + decimalPart}M`
  }

  return valueNumber.toLocaleString('en-US')
}

/**
 * Converts camelCase string to a string with spaces before capitalized letters
 * @example
 * camelCaseToSpaced('camelCase') // returns 'camel Case'
 * camelCaseToSpaced('someCamelCaseText') // returns 'some Camel Case Text'
 * camelCaseToSpaced('InvestmentHighlights') // returns 'Investment Highlights'
 */
export function camelCaseToSpaced(text: string): string {
  return text.replace(/([a-z])([A-Z])/g, '$1 $2')
}

export function formatPercentageValueBN(value: bigint) {
  return (value / BigInt(100)).toLocaleString('en-US', {
    maximumFractionDigits: 2,
  })
}

export function formatPercentageValue(value: number) {
  return (value / 100).toLocaleString('en-US', {
    maximumFractionDigits: 2,
  })
}

//TODO: create <Price />
export const formatPrice = ({
  price,
  tokenDecimals,
  roundingDecimals,
  minRoundingDecimals = 0,
  currency,
  roundStrategy,
}: {
  price: bigint | number
  tokenDecimals: number
  roundingDecimals: number
  minRoundingDecimals?: number
  currency?: string
  roundStrategy?: 'round' | 'floor' | 'ceil'
}): string => {
  if (!price) {
    return currency ? `${currency} 0` : '0'
  }

  const priceConverted = parseFloat(formatUnits(price, tokenDecimals))

  const priceTruncated = cutDecimalsWithoutRounding(priceConverted, roundingDecimals)
  let adjustedPrice = priceTruncated

  if (roundStrategy) {
    if (roundStrategy === 'ceil') {
      adjustedPrice = roundToPrecision(priceConverted, roundingDecimals, 'ceil')
    } else if (roundStrategy === 'floor') {
      adjustedPrice = roundToPrecision(priceConverted, roundingDecimals, 'floor')
    } else {
      adjustedPrice = roundToPrecision(priceConverted, roundingDecimals, 'round')
    }
  }

  const priceFormatted = adjustedPrice.toLocaleString('en-US', {
    maximumFractionDigits: roundingDecimals,
    minimumFractionDigits: minRoundingDecimals,
  })

  return currency ? `${currency} ${priceFormatted}` : priceFormatted
}

export const formatUnits = (value: bigint | number, decimals: number) => {
  let display = value.toString()

  const negative = display.startsWith('-')
  if (negative) display = display.slice(1)

  display = display.padStart(decimals, '0')

  const integer = display.slice(0, display.length - decimals) || '0'
  let fraction = display.slice(display.length - decimals)
  fraction = fraction.replace(/(0+)$/, '')
  return `${negative ? '-' : ''}${integer}${fraction ? `.${fraction}` : ''}`
}

export const formatStrapiNumber = (
  value?: string | number | bigint | null,
  options: {
    decimals?: number
    emptyMessage?: string
  } = {},
) => {
  const { emptyMessage = 'N/A', decimals = 4 } = options

  if (isNil(value)) {
    return emptyMessage
  }

  return formatPrice({
    price: Number(value),
    roundingDecimals: decimals,
    tokenDecimals: 0,
  })
}

export const formatNumberWithoutRounding = (value: bigint | number) => {
  const valueStr = Number(value).toString()

  if (!valueStr.includes('.')) {
    return formatNumber(Number(value))
  }

  const [integers, decimals] = valueStr.trim().split('.')

  return `${formatNumber(Number(integers))}.${decimals}`
}

const roundToPrecision = (
  value: number,
  precision: number,
  strategy: 'round' | 'floor' | 'ceil',
) => {
  const factor = Math.pow(10, precision)

  if (strategy === 'floor' && value < 0) {
    return Math.ceil(value * factor) / factor
  }

  if (strategy === 'ceil' && value < 0) {
    return Math.floor(value * factor) / factor
  }

  return Math[strategy](value * factor) / factor
}

/**
 * Converts a bigint value from wei (smallest unit) to actual token amount
 * @param weiValue - The bigint value in wei format
 * @param decimals - Number of decimal places (default: 18 for ETH/ERC20 tokens)
 * @returns The converted number in actual token units
 *
 * @example
 * convertWeiToTokens(44400000000000000000000n) // Returns 44400
 * convertWeiToTokens(1000000000000000000n) // Returns 1
 */
export const convertWeiToTokens = (weiValue: bigint, decimals: number = 18): number => {
  // Convert to string to avoid floating point precision issues
  // const weiString = weiValue.toString()
  const divisor = BigInt(10 ** decimals)

  // Use BigInt division for precision, then convert to number
  const quotient = weiValue / divisor
  const remainder = weiValue % divisor

  // If there's a remainder, we need to handle the decimal part
  if (remainder === 0n) {
    return Number(quotient)
  }

  // For cases with decimals, use string manipulation to avoid precision errors
  const quotientStr = quotient.toString()
  const remainderStr = remainder.toString().padStart(decimals, '0')

  const trimmedRemainder = remainderStr.replace(/0+$/, '')

  if (trimmedRemainder === '') {
    return Number(quotientStr)
  }

  return parseFloat(`${quotientStr}.${trimmedRemainder}`)
}
